import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import caseStudies from "../../../data/caseStudies";
import { useLanguage } from "../../../i18n/LanguageContext";

const Work = () => {
  const { t, lang } = useLanguage();
  const [active, setActive] = useState(caseStudies[0].id);
  const entry = caseStudies.find((c) => c.id === active) || caseStudies[0];
  // Merge the language-neutral fields with the copy for the active language.
  const study = {
    ...entry,
    ...entry[lang],
    period: entry.period[lang],
    stack: entry.stack[lang],
  };

  return (
    <section className="font-inter relative z-10 mx-auto mt-36 w-full max-w-5xl px-6 md:mt-52" id="work">
      <div className="text-center">
        <motion.h2
          initial={{ y: -16, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ type: "spring", stiffness: 100, damping: 12 }}
          className="text-2xl font-bold text-white"
        >
          <span className="bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent">
            {t.work.title}
          </span>
        </motion.h2>
        <motion.div
          className="mx-auto mt-2 h-[2px] w-16 bg-gradient-to-r from-indigo-400 to-purple-500"
          initial={{ width: 0 }}
          whileInView={{ width: 64 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 0.8, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mx-auto mt-5 max-w-xl text-base font-light text-gray-300"
        >
          {t.work.subtitle}
        </motion.p>
      </div>

      {/* Company switcher */}
      <div
        role="tablist"
        aria-label={t.work.tablist}
        className="mt-10 flex flex-wrap items-center justify-center gap-2"
      >
        {caseStudies.map((c) => {
          const isActive = c.id === active;
          return (
            <button
              key={c.id}
              role="tab"
              id={`tab-${c.id}`}
              aria-selected={isActive}
              aria-controls={`panel-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`flex items-center gap-2.5 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
                isActive
                  ? "border-indigo-500/60 bg-[#252438] text-white shadow-[0_0_24px_-8px_rgba(116,56,255,0.8)]"
                  : "border-[rgba(67,65,85,0.5)] bg-[rgba(255,255,255,0.02)] text-[rgba(245,245,245,0.55)] hover:border-[rgba(120,118,150,0.7)] hover:text-white"
              }`}
            >
              <img
                src={c.logo}
                alt=""
                width={22}
                height={22}
                className={`rounded-full transition-opacity duration-300 ${
                  isActive ? "opacity-100" : "opacity-60"
                }`}
              />
              {c.company}
            </button>
          );
        })}
      </div>

      {/* Case study panel */}
      <AnimatePresence mode="wait">
        <motion.article
          key={study.id}
          id={`panel-${study.id}`}
          role="tabpanel"
          aria-labelledby={`tab-${study.id}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 overflow-hidden rounded-xl border-[0.5px] border-[rgba(67,65,85,0.5)] bg-[rgba(7,6,14,0.6)] p-7 backdrop-blur-xl md:p-10"
        >
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-indigo-500/0 via-indigo-500/70 to-purple-500/0" />

          <header className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-white md:text-2xl">{study.tagline}</h3>
              <p className="mt-1.5 text-sm text-[rgba(245,245,245,0.5)]">
                {study.role} ·{" "}
                <a
                  href={study.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-400 transition-colors hover:text-indigo-300"
                >
                  {study.company}
                </a>{" "}
                · {study.location}
              </p>
            </div>
            <span className="whitespace-nowrap rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.04)] px-3.5 py-1.5 text-xs font-medium text-[rgba(245,245,245,0.6)]">
              {study.period}
            </span>
          </header>

          {/* Metrics — the reason this section exists */}
          <dl className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-[rgba(67,65,85,0.45)] bg-[rgba(67,65,85,0.45)] sm:grid-cols-3">
            {study.metrics.map((m) => (
              <div
                key={m.label}
                className="flex flex-col items-center bg-[#1a1928] px-4 py-5 text-center"
              >
                <dd className="bg-gradient-to-r from-indigo-300 to-purple-400 bg-clip-text text-xl font-black text-transparent md:text-2xl">
                  {m.value}
                </dd>
                <dt className="mt-1.5 text-[11px] uppercase tracking-wider text-[rgba(245,245,245,0.5)]">
                  {m.label}
                </dt>
              </div>
            ))}
          </dl>

          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-[rgba(245,245,245,0.42)]">
                {t.work.problem}
              </h4>
              <p className="mt-3 text-sm leading-relaxed text-[rgba(245,245,245,0.72)]">
                {study.problem}
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-[rgba(245,245,245,0.42)]">
                {t.work.whatIDid}
              </h4>
              <ul className="mt-3 space-y-2.5">
                {study.work.map((item) => (
                  <li
                    key={item}
                    className="flex gap-2.5 text-sm leading-relaxed text-[rgba(245,245,245,0.72)]"
                  >
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-indigo-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <ul className="mt-8 flex flex-wrap gap-2 border-t border-[rgba(67,65,85,0.4)] pt-6">
            {study.stack.map((tech) => (
              <li
                key={tech}
                className="rounded-md border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-2.5 py-1 text-xs text-[rgba(245,245,245,0.6)]"
              >
                {tech}
              </li>
            ))}
          </ul>
        </motion.article>
      </AnimatePresence>
    </section>
  );
};

export default Work;
